export const SOURCE_FORM_VALUES = ['BORN_DIGITAL', 'SCANNED_PAPER', 'MIXED', 'UNKNOWN'];

export const PAPER_ORIGINAL_VALUES = ['NOT_APPLICABLE', 'NONE', 'CONTINUING', 'UNRESOLVED'];

export const RELATIONSHIP_TYPES = [
  'PRIMARY',
  'SUPPORTS',
  'SECURES',
  'AMENDS',
  'SUPERSEDES',
  'DERIVED_FROM',
  'EVIDENCE_FOR',
];

export const EVIDENCE_FACT_CODES = [
  'EXPRESS_AGREEMENT',
  'REAL_PROPERTY_LINK',
  'SIGNATURE_EVIDENCE',
  'SINGLE_COPY_INTENT',
];

export const PACKAGE_ONLY_FACT_CODES = ['CONTROL_PARTY', 'LEGAL_CAPACITY', 'PRIMARY_DOCUMENT'];

/** Originator fact confirmation must not submit these. */
export const FORBIDDEN_FACT_CODES = [
  'PROVIDER_RECOMMENDATION',
  'PROVIDER_OUTCOME',
  'RECOMMENDATION',
  'REVIEW_RECOMMENDATION',
  'AUTHORITY_STATUS',
  'AUTHORITY_STATE',
  'CUSTODIAN',
  'CUSTODIAN_AUTHORITY',
  'CUSTODY_STATUS',
  'AUTHORITATIVE_STATUS',
  'AUTHORITATIVE_STORE_URI',
  'ADMISSION_STATE',
  'VAULT_ADMISSION',
  'READINESS_STATUS',
  'INTENDED_TREATMENT',
  'LEGAL_REGIME',
  'LEGAL_REGIMES',
  'CONTROL_PROFILE',
  'POLICY_HASH',
  'ADMISSION_POLICY_HASH',
  'ASSESSMENT_HASH',
  'RULE_RESULT',
  'ADMISSION_RULE_RESULT',
  'ADMISSION_PROFILE',
  'ADMISSION_PROFILE_ID',
  'VAULT_ROLE',
  'CLASSIFICATION_SOURCE',
];

const FORBIDDEN = new Set(FORBIDDEN_FACT_CODES);
const PACKAGE_ONLY = new Set(PACKAGE_ONLY_FACT_CODES);
const EVIDENCE = new Set(EVIDENCE_FACT_CODES);

function text(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

function asEvidenceBoolean(value) {
  if (value === true || value === 1) return 'true';
  if (value === false || value === 0) return 'false';
  const token = String(value).trim().toLowerCase();
  if (token === 'true' || token === '1' || token === 'yes') return 'true';
  if (token === 'false' || token === '0' || token === 'no') return 'false';
  return null;
}

function evidenceDraftValue(admission, code) {
  const facts = admission?.facts || [];
  const match = facts.find(
    (item) => String(item?.fact_code || '').trim().toUpperCase() === code
  );
  const fromFact = match ? asEvidenceBoolean(match.value) : null;
  if (fromFact) return fromFact;

  const codes = admission?.confirmed_fact_codes || [];
  if (codes.some((item) => String(item).trim().toUpperCase() === code)) {
    return 'true';
  }
  return '';
}

/** Stable key so a new admission object with the same row does not wipe an in-progress draft. */
export function admissionDraftResetKey(admission, documentId) {
  return `${Number(admission?.row_version) || 0}|${documentId || ''}`;
}

export function mergeConfirmedFactCodes(base, extra) {
  const codes = [];
  const seen = new Set();
  for (const item of [...(base?.confirmed_fact_codes || []), ...(extra?.confirmed_fact_codes || [])]) {
    const token = String(item || '').trim().toUpperCase();
    if (!token || seen.has(token)) continue;
    seen.add(token);
    codes.push(token);
  }
  const hadCodes =
    Array.isArray(base?.confirmed_fact_codes) || Array.isArray(extra?.confirmed_fact_codes);
  if (!hadCodes) return base || extra;
  if (!base) return extra ? { ...extra, confirmed_fact_codes: codes } : extra;
  return { ...base, confirmed_fact_codes: codes };
}

export function baselineFactDraft(admission, documentId) {
  const document = (admission?.document_assessments || []).find(
    (row) => documentId && String(row.document_id) === String(documentId)
  );
  const relationship = (admission?.relationships || []).find(
    (item) => documentId && String(item.from_document_id) === String(documentId)
  );
  return {
    CONTROL_PARTY: text(admission?.control_party),
    LEGAL_CAPACITY: text(admission?.legal_capacity),
    SOURCE_FORM: text(admission?.source_form),
    PAPER_ORIGINAL_STATUS: text(admission?.paper_original_status),
    PRIMARY_DOCUMENT: text(admission?.primary_document_id),
    documentSourceForm: text(document?.source_form),
    documentPaperStatus: '',
    relationshipType: text(relationship?.relationship_type),
    relationshipTarget: text(relationship?.to_document_id),
    EXPRESS_AGREEMENT: evidenceDraftValue(admission, 'EXPRESS_AGREEMENT'),
    REAL_PROPERTY_LINK: evidenceDraftValue(admission, 'REAL_PROPERTY_LINK'),
    SIGNATURE_EVIDENCE: evidenceDraftValue(admission, 'SIGNATURE_EVIDENCE'),
    SINGLE_COPY_INTENT: evidenceDraftValue(admission, 'SINGLE_COPY_INTENT'),
  };
}

function relationshipValue(type, target) {
  if (!type) return null;
  if (type === 'PRIMARY') {
    return { relationship_type: 'PRIMARY', to_document_id: null };
  }
  return { relationship_type: type, to_document_id: target || null };
}

function pushFact(facts, missingReasons, invalid, entry, { priorExists, reason, forceReasons }) {
  const needsReason = priorExists || forceReasons;
  const trimmedReason = text(reason);
  if (needsReason && !trimmedReason) {
    missingReasons.push(entry.fact_code);
  }
  if (FORBIDDEN.has(entry.fact_code)) {
    invalid.push(entry.fact_code);
    return;
  }
  facts.push({
    fact_code: entry.fact_code,
    document_id: entry.document_id,
    value: entry.value,
    source: 'USER_CONFIRMATION',
    reason_code: needsReason ? trimmedReason || null : null,
  });
}

export function diffAdmissionFacts(baseline, draft, reasons = {}, documentId, options = {}) {
  const facts = [];
  const missingReasons = [];
  const invalid = [];
  const forceReasons = Boolean(options.forceReasons);
  const currentDocumentId = documentId ? String(documentId) : null;

  function consider(code, before, after, { document_id, value, allowEmpty = false, reasonKey }) {
    if (text(before) === text(after)) return;
    if (!allowEmpty && text(after) === '') {
      invalid.push(code);
      return;
    }
    pushFact(
      facts,
      missingReasons,
      invalid,
      { fact_code: code, document_id, value },
      {
        priorExists: text(before) !== '',
        reason: reasons[reasonKey || code],
        forceReasons,
      }
    );
  }

  consider('CONTROL_PARTY', baseline.CONTROL_PARTY, draft.CONTROL_PARTY, {
    document_id: null,
    value: text(draft.CONTROL_PARTY),
  });
  consider('LEGAL_CAPACITY', baseline.LEGAL_CAPACITY, draft.LEGAL_CAPACITY, {
    document_id: null,
    value: text(draft.LEGAL_CAPACITY),
  });
  consider('SOURCE_FORM', baseline.SOURCE_FORM, draft.SOURCE_FORM, {
    document_id: null,
    value: text(draft.SOURCE_FORM),
  });
  consider('PAPER_ORIGINAL_STATUS', baseline.PAPER_ORIGINAL_STATUS, draft.PAPER_ORIGINAL_STATUS, {
    document_id: null,
    value: text(draft.PAPER_ORIGINAL_STATUS),
  });
  consider('PRIMARY_DOCUMENT', baseline.PRIMARY_DOCUMENT, draft.PRIMARY_DOCUMENT, {
    document_id: null,
    value: text(draft.PRIMARY_DOCUMENT),
  });

  EVIDENCE_FACT_CODES.forEach((code) => {
    const before = baseline[code];
    const after = draft[code];
    if (after !== 'true' && after !== 'false') return;
    if (text(before) === text(after)) return;
    pushFact(
      facts,
      missingReasons,
      invalid,
      { fact_code: code, document_id: null, value: after === 'true' },
      {
        priorExists: before === 'true' || before === 'false',
        reason: reasons[code],
        forceReasons,
      }
    );
  });

  if (currentDocumentId) {
    consider('SOURCE_FORM', baseline.documentSourceForm, draft.documentSourceForm, {
      document_id: currentDocumentId,
      value: text(draft.documentSourceForm),
      reasonKey: 'DOCUMENT_SOURCE_FORM',
    });
    if (text(baseline.documentPaperStatus) !== text(draft.documentPaperStatus) && text(draft.documentPaperStatus)) {
      pushFact(
        facts,
        missingReasons,
        invalid,
        {
          fact_code: 'PAPER_ORIGINAL_STATUS',
          document_id: currentDocumentId,
          value: text(draft.documentPaperStatus),
        },
        {
          priorExists: text(baseline.documentPaperStatus) !== '',
          reason: reasons.DOCUMENT_PAPER_ORIGINAL_STATUS,
          forceReasons,
        }
      );
    }

    const beforeRelationship = relationshipValue(
      baseline.relationshipType,
      baseline.relationshipTarget
    );
    const afterRelationship = relationshipValue(draft.relationshipType, draft.relationshipTarget);
    const sameRelationship =
      JSON.stringify(beforeRelationship) === JSON.stringify(afterRelationship);
    if (!sameRelationship && afterRelationship) {
      if (
        afterRelationship.relationship_type !== 'PRIMARY' &&
        !afterRelationship.to_document_id
      ) {
        invalid.push('DOCUMENT_RELATIONSHIP');
      } else if (
        afterRelationship.to_document_id &&
        afterRelationship.to_document_id === currentDocumentId
      ) {
        invalid.push('DOCUMENT_RELATIONSHIP');
      } else {
        pushFact(
          facts,
          missingReasons,
          invalid,
          {
            fact_code: 'DOCUMENT_RELATIONSHIP',
            document_id: currentDocumentId,
            value: afterRelationship,
          },
          {
            priorExists: Boolean(beforeRelationship),
            reason: reasons.DOCUMENT_RELATIONSHIP,
            forceReasons,
          }
        );
      }
    }
  }

  return { facts, missingReasons, invalid };
}

export function buildAdmissionFactsRequest(rowVersion, facts) {
  const version = Number(rowVersion);
  if (!Number.isInteger(version) || version < 1) {
    throw new Error('row_version is required');
  }
  if (!Array.isArray(facts) || facts.length === 0) {
    throw new Error('facts must contain at least one item');
  }

  const bodyFacts = facts.map((fact) => {
    const code = text(fact?.fact_code);
    if (!code || FORBIDDEN.has(code)) {
      throw new Error(`${code || 'Fact'} is not an Originator factual field`);
    }
    if (code === 'DOCUMENT_RELATIONSHIP' && !fact.document_id) {
      throw new Error('DOCUMENT_RELATIONSHIP requires a document');
    }
    if (EVIDENCE.has(code) && typeof fact.value !== 'boolean') {
      throw new Error(`${code} must be true or false`);
    }
    return {
      fact_code: code,
      document_id: PACKAGE_ONLY.has(code) || EVIDENCE.has(code) ? null : fact.document_id ?? null,
      value: fact.value,
      source: 'USER_CONFIRMATION',
      reason_code: fact.reason_code ?? null,
    };
  });

  return {
    row_version: version,
    facts: bodyFacts,
  };
}
